// TODO: (vliu) This is used in architect only and can be removed after we update architect
// to use same list style as recordings page

/* eslint-disable react/button-has-type */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components/macro';

import { DropdownData, DropdownSectionInfo } from '../types';

const DropdownListContainer = styled.div<{
  alwaysVisible: boolean;
}>`
  /* Hide dropdown list by default. Will be overriden to flex when form
  is clicked on focused */
  display: ${(props) => (props.alwaysVisible ? 'flex' : 'none')};
  flex-direction: column;

  ${(props) =>
    props.alwaysVisible
      ? ''
      : 'position: absolute;top: 100%;left: 0; max-height: 250px;'};
  /* Position the lists below the form and keep the same width */
  width: 100%;

  /* Fixed height scrolly container */
  overflow-y: auto;
  overflow-x: hidden;

  /* Styling */
  background-color: ${(props) => props.theme.bgColor};
  filter: drop-shadow(0px 2px 4px rgba(255, 255, 255, 0.1));
  border-radius: 0px 0px 4px 4px;

  /* Make it an overlay */
  z-index: var(--z-overlay);
`;

const StyledDropdownSection = styled.div`
  padding: 4px 0px;

  /* Title for the Dropdown section */
  div.title {
    padding-left: 16px;
    padding-bottom: 4px;
    padding-top: 4px;
    color: ${(props) => props.theme.titleColor};

    /* Have section title be sticky the user always knows which section
    of the dropdown list they're in */
    position: sticky;
    top: 0;
    /* Ensure there's a background color so the list items scroll
    underneath */
    background-color: ${(props) => props.theme.bgColor};
    /* Add a line at the bottom to help see what is sticky and what isn't */
    border-bottom: 1px solid ${(props) => props.theme.delimiterColorLight};
  }
`;

const StyledOrderedList = styled.ol`
  /* Reset ol default style */
  margin: 0;
  padding: 0;
  list-style: none;

  /* Style the elements inside */
  li {
    display: flex;
    align-items: center;
    height: 28px;
    padding-left: 16px;

    /* Give feedback when the element has already been selected */
    &.already-selected {
      color: ${(props) => props.theme.textDisabledColor};
    }

    /* Hover feedback when not already selected */
    &:hover:not(.already-selected) {
      background-color: ${(props) => props.theme.hoverBgColor};
      cursor: pointer;
    }

    &.keyboard-focused {
      background-color: ${(props) => props.theme.keyboardFocusBgColor};
    }

    & > button {
      width: 100%;
      height: 100%;

      /* This is text here, so the button shouldn't be styled */
      background-color: transparent;
      border: 0;
      padding: 0;
      text-align: left;

      color: ${(props) => props.theme.textDefaultColor};
      cursor: pointer;
    }
  }
`;

// Render as bold the parts of a string that the user did not search
// for. This is common UX practice for autocompletion in forms (e.g.
// Amazon)
const boldPartOfString = (str: string, substr: string) => {
  // When no substring is given, don't bold anything
  if (!substr) {
    return str;
  }

  // Look for all occurences of the substring in the string
  const occurences: number[] = [];
  let occurenceIndex = str.toLowerCase().indexOf(substr.toLowerCase());
  while (occurenceIndex !== -1 && occurenceIndex !== str.length) {
    occurences.push(occurenceIndex);
    const newOccurenceIndex = str
      .toLowerCase()
      .indexOf(substr.toLowerCase(), occurenceIndex + 1);
    occurenceIndex = newOccurenceIndex;
  }

  const htmlTags: JSX.Element[] = [];
  let currentIndex = 0;

  occurences.forEach((substrIndex) => {
    // render as bold text what is not part of the substring
    htmlTags.push(
      <b key={`bold-${currentIndex}`}>
        {str.substring(currentIndex, substrIndex)}
      </b>
    );
    // render the substring as normal text
    htmlTags.push(
      <React.Fragment key={`reg-${substrIndex}`}>
        {str.substring(substrIndex, substrIndex + substr.length)}
      </React.Fragment>
    );
    currentIndex = substrIndex + substr.length;
  });
  // Final occurence after the last substring
  htmlTags.push(
    <b key={`bold-${currentIndex}`}>{str.substring(currentIndex)}</b>
  );

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{htmlTags}</>;
};

const DropdownList: React.FC<{
  options: Map<DropdownSectionInfo, DropdownData[]>;
  onClicked: (option: string) => void;
  userInput: string;
  keyboardFocusedOption: string;
  alreadySelectedOptions: string[];
  alwaysVisible?: boolean;
}> = ({
  options,
  onClicked,
  userInput,
  keyboardFocusedOption,
  alreadySelectedOptions,
  alwaysVisible = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const keyboardRef = useRef<HTMLLIElement>(null);
  const sectionTitleRef = useRef<HTMLDivElement>(null);

  // When the user changes the focused option using a keyboard, make
  // sure their focus stays visible by scrolling the container
  useEffect(() => {
    if (
      containerRef.current &&
      keyboardRef.current &&
      sectionTitleRef.current
    ) {
      const containerHeight = containerRef.current.clientHeight;
      const { scrollTop } = containerRef.current;
      const scrollBottom = scrollTop + containerHeight;
      // Take into account the sticky title at the top when dealing with
      // the offset at the top
      const thresholdVisibilityTop =
        keyboardRef.current.offsetTop - sectionTitleRef.current.clientHeight;
      // Make sure the item is fully visible at the bottom
      const thresholdVisibilityBottom =
        keyboardRef.current.offsetTop + keyboardRef.current.clientHeight;

      if (scrollTop > thresholdVisibilityTop) {
        containerRef.current.scrollTo(0, thresholdVisibilityTop);
      } else if (scrollBottom < thresholdVisibilityBottom) {
        containerRef.current.scrollTo(
          0,
          thresholdVisibilityBottom - containerHeight
        );
      }
    }
  }, [keyboardFocusedOption, options]);

  return (
    <DropdownListContainer alwaysVisible={alwaysVisible} ref={containerRef}>
      {[...options].map(([section, sectionDataList], index) => (
        <StyledDropdownSection key={section.name}>
          {/* Here we assume all the section titles have the same dimensions so we just care about the first one */}
          <div className="title" ref={index === 0 ? sectionTitleRef : null}>
            {section.name}
          </div>
          <StyledOrderedList>
            {sectionDataList.map((data) =>
              alreadySelectedOptions.includes(data.name) ? (
                <li className="already-selected " key={data.name}>
                  {boldPartOfString(data.name, userInput)}
                </li>
              ) : (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                <li
                  key={data.name}
                  onMouseDown={(ev) => {
                    // Prevent the list item from stealing the focus
                    // from the DropdownInput. Keeping the focus on the
                    // input provides better experience and prompt users
                    // to select more elements. It also simplifies
                    // things for styling and reduces the chance of
                    // flicker. We could turn this into a flag later if
                    // we need the flexibility
                    ev.preventDefault();
                  }}
                  onClick={() => {
                    onClicked(data.name);
                  }}
                  className={
                    keyboardFocusedOption === data.name
                      ? 'keyboard-focused'
                      : ''
                  }
                  ref={keyboardFocusedOption === data.name ? keyboardRef : null}
                >
                  <button className="horizontalScroll">
                    {boldPartOfString(data.name, userInput)}
                  </button>
                </li>
              )
            )}
          </StyledOrderedList>
        </StyledDropdownSection>
      ))}
    </DropdownListContainer>
  );
};

export { DropdownList, DropdownListContainer };
